import { useState, useEffect, useCallback } from "react";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import {
  MapPin, Clock, LogIn, LogOut, CheckCircle2, XCircle, Loader2,
  History, ChevronRight, Wifi, WifiOff, Shield, AlertTriangle,
  Smartphone, Download, Navigation, Timer, ArrowLeft, Eye, EyeOff, BellRing, CalendarPlus
} from "lucide-react";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import { Toaster } from "../components/ui/sonner";

const ax = (t) => ({ headers: { Authorization: `Bearer ${t}` } });

/* ====== Mobile Login ====== */
const MobileLogin = ({ onLogin, branding }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPw, setShowPw] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem("mobile_token", res.data.token);
      onLogin(res.data.token);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al iniciar sesion");
    } finally { setLoading(false); }
  };

  const primaryColor = branding?.wl_primary_color || "#059669";

  return (
    <div className="min-h-screen flex flex-col" style={{ background: `linear-gradient(180deg, ${primaryColor}15 0%, #fafbfc 50%)` }}>
      <div className="flex-1 flex flex-col items-center justify-center px-6 py-12">
        <img src={branding?.wl_logo_url || "/logo-humanoz.png"} alt="Logo" className="h-16 w-auto mb-3" data-testid="mobile-logo" />
        <h1 className="text-xl font-bold text-slate-900 mb-1" data-testid="mobile-app-name">{branding?.wl_app_name || "Human-OZ"}</h1>
        <p className="text-sm text-slate-500 mb-8">Control de Asistencia</p>

        <form onSubmit={handleSubmit} className="w-full max-w-sm space-y-4">
          <div>
            <Input type="email" placeholder="Correo electronico" value={email} onChange={e => setEmail(e.target.value)}
              className="h-12 rounded-xl text-base" autoComplete="email" data-testid="mobile-email" />
          </div>
          <div className="relative">
            <Input type={showPw ? "text" : "password"} placeholder="Contrasena" value={password} onChange={e => setPassword(e.target.value)}
              className="h-12 rounded-xl text-base pr-12" autoComplete="current-password" data-testid="mobile-password" />
            <button type="button" className="absolute right-3 top-3 text-slate-400" onClick={() => setShowPw(!showPw)}>
              {showPw ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
            </button>
          </div>
          <Button type="submit" disabled={loading} className="w-full h-12 rounded-xl text-base font-semibold shadow-lg"
            style={{ backgroundColor: primaryColor }} data-testid="mobile-login-btn">
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Iniciar Sesion"}
          </Button>
        </form>

        <p className="text-xs text-slate-400 mt-8 text-center">Usa tu cuenta de {branding?.wl_app_name || "Human-OZ"} para acceder</p>
      </div>
    </div>
  );
};

/* ====== GPS Hook ====== */
const useGPS = () => {
  const [coords, setCoords] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const getLocation = useCallback(() => {
    if (!navigator.geolocation) { setError("GPS no disponible"); return; }
    setLoading(true); setError(null);
    navigator.geolocation.getCurrentPosition(
      (pos) => { setCoords({ latitude: pos.coords.latitude, longitude: pos.coords.longitude, accuracy: pos.coords.accuracy }); setLoading(false); },
      (err) => { setError(err.code === 1 ? "Permiso de ubicacion denegado. Activa el GPS." : err.code === 2 ? "Ubicacion no disponible" : "Tiempo de espera agotado"); setLoading(false); },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 5000 }
    );
  }, []);

  return { coords, error, loading, getLocation };
};

/* ====== Main Mobile App ====== */
const MobileApp = () => {
  const [token, setToken] = useState(localStorage.getItem("mobile_token"));
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [branding, setBranding] = useState(null);
  const [view, setView] = useState("home"); // home | history
  const [installPrompt, setInstallPrompt] = useState(null);
  const [preferences, setPreferences] = useState({ email_opt_in: true, whatsapp_opt_in: false, internal_chat_opt_in: true, calendly_connected: false });
  const [internalNotifications, setInternalNotifications] = useState([]);
  const gps = useGPS();

  // Capture PWA install prompt
  useEffect(() => {
    const handler = (e) => { e.preventDefault(); setInstallPrompt(e); };
    window.addEventListener("beforeinstallprompt", handler);
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  const fetchBranding = useCallback(async (t) => {
    try {
      const res = await axios.get(`${API}/company/white-label`, ax(t));
      if (res.data?.white_label) setBranding(res.data);
    } catch {}
  }, []);

  const fetchStatus = useCallback(async (t) => {
    try {
      const res = await axios.get(`${API}/mobile/status`, ax(t));
      setStatus(res.data);
      if (res.data?.branding) setBranding(res.data.branding);
      const [prefsRes, notifRes] = await Promise.all([
        axios.get(`${API}/user/notification-consent`, ax(t)).catch(() => ({ data: null })),
        axios.get(`${API}/internal-notifications`, ax(t)).catch(() => ({ data: [] }))
      ]);
      if (prefsRes.data) setPreferences(prefsRes.data);
      setInternalNotifications(notifRes.data || []);
    } catch (err) {
      if (err.response?.status === 401) { localStorage.removeItem("mobile_token"); setToken(null); }
    } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    if (token) { fetchStatus(token); fetchBranding(token); gps.getLocation(); }
    else setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, fetchStatus, fetchBranding]);

  const handleClockIn = async () => {
    if (!gps.coords) { gps.getLocation(); toast.error("Obteniendo ubicacion..."); return; }
    setActionLoading(true);
    try {
      const res = await axios.post(`${API}/mobile/clock-in`, { latitude: gps.coords.latitude, longitude: gps.coords.longitude }, ax(token));
      toast.success(`Entrada registrada a las ${res.data.time}`);
      fetchStatus(token);
    } catch (err) { toast.error(err.response?.data?.detail || "Error al registrar entrada"); }
    finally { setActionLoading(false); }
  };

  const handleClockOut = async () => {
    if (!gps.coords) { gps.getLocation(); toast.error("Obteniendo ubicacion..."); return; }
    setActionLoading(true);
    try {
      const res = await axios.post(`${API}/mobile/clock-out`, { latitude: gps.coords.latitude, longitude: gps.coords.longitude }, ax(token));
      toast.success(`Salida registrada. Total: ${res.data.total_hours}h`);
      fetchStatus(token);
    } catch (err) { toast.error(err.response?.data?.detail || "Error al registrar salida"); }
    finally { setActionLoading(false); }
  };

  const handleLogout = () => { localStorage.removeItem("mobile_token"); setToken(null); setStatus(null); };
  const savePreferences = async () => {
    try {
      await axios.post(`${API}/user/notification-consent`, preferences, ax(token));
      toast.success("Preferencias guardadas");
    } catch {
      toast.error("No se pudieron guardar");
    }
  };
  const connectCalendly = async () => {
    try {
      const response = await axios.post(`${API}/oauth/calendly/connect/start`, {}, ax(token));
      if (response.data.status === "ready" && response.data.authorization_url) window.open(response.data.authorization_url, "_blank");
      else toast.message(`Calendly: ${response.data.status}`);
    } catch {
      toast.error("No se pudo iniciar Calendly");
    }
  };

  const primaryColor = branding?.wl_primary_color || "#059669";

  if (!token) return <MobileLogin onLogin={(t) => { setToken(t); setLoading(true); fetchStatus(t); fetchBranding(t); gps.getLocation(); }} branding={branding} />;
  if (loading) return <div className="min-h-screen flex items-center justify-center bg-[#fafbfc]"><Loader2 className="w-8 h-8 animate-spin" style={{ color: primaryColor }} /></div>;

  const isClockedIn = status?.clocked_in;
  const todayEntry = status?.today_entry;
  const closestGeofence = status?.geofences?.[0];

  return (
    <div className="min-h-screen bg-[#fafbfc] pb-24">
      <Toaster position="top-center" richColors />

      {/* ====== HEADER ====== */}
      <header className="sticky top-0 z-50 px-5 py-4 bg-white/80 backdrop-blur-xl border-b border-slate-100" data-testid="mobile-header">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <img src={status?.employee?.work_center_logo_url || branding?.wl_logo_url || "/logo-humanoz.png"} alt="Logo" className="h-8 w-auto" />
            {branding?.wl_app_name && branding.wl_app_name !== "Human-OZ" && <span className="text-sm font-bold text-slate-800">{branding.wl_app_name}</span>}
          </div>
          <button onClick={handleLogout} className="text-xs text-slate-400 font-medium" data-testid="mobile-logout">Salir</button>
        </div>
      </header>

      {/* ====== EMPLOYEE INFO ====== */}
      <div className="px-5 pt-5 pb-3">
        <p className="text-lg font-bold text-slate-900" data-testid="mobile-employee-name">{status?.employee?.name}</p>
        <p className="text-sm text-slate-500">{status?.employee?.position}{status?.employee?.department ? ` - ${status.employee.department}` : ""}</p>
        {status?.employee?.work_center_name && <p className="text-xs text-sky-600 mt-1" data-testid="mobile-work-center-name">{status.employee.work_center_name}</p>}
        {status?.shift && (
          <div className="flex items-center gap-1.5 mt-2">
            <Timer className="w-3.5 h-3.5 text-slate-400" />
            <span className="text-xs text-slate-500">{status.shift.name}: {status.shift.start_time} - {status.shift.end_time}</span>
          </div>
        )}
      </div>

      {/* ====== GPS STATUS ====== */}
      <div className="px-5 mb-4">
        <div className={`flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-medium ${gps.coords ? "bg-emerald-50 text-emerald-700" : gps.error ? "bg-red-50 text-red-700" : "bg-slate-100 text-slate-500"}`} data-testid="gps-status">
          {gps.loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : gps.coords ? <Navigation className="w-3.5 h-3.5" /> : gps.error ? <WifiOff className="w-3.5 h-3.5" /> : <Wifi className="w-3.5 h-3.5" />}
          {gps.loading ? "Obteniendo ubicacion..." : gps.coords ? `GPS activo (precision: ${Math.round(gps.coords.accuracy)}m)` : gps.error || "Esperando GPS..."}
          {!gps.coords && !gps.loading && <button onClick={gps.getLocation} className="ml-auto underline">Reintentar</button>}
        </div>
      </div>

      {/* ====== GEOFENCE INFO ====== */}
      {closestGeofence && gps.coords && (
        <div className="px-5 mb-4">
          <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-blue-50 text-blue-700 text-xs font-medium">
            <MapPin className="w-3.5 h-3.5" />
            <span>{closestGeofence.name} (radio: {closestGeofence.radius_meters}m)</span>
          </div>
        </div>
      )}

      {/* ====== TABS ====== */}
      <div className="flex gap-2 px-5 mb-5">
        <button onClick={() => setView("home")} className={`flex-1 py-2.5 rounded-xl text-sm font-semibold transition-all ${view === "home" ? "text-white shadow-md" : "bg-slate-100 text-slate-500"}`} style={view === "home" ? { backgroundColor: primaryColor } : {}} data-testid="tab-home">
          <Clock className="w-4 h-4 inline mr-1.5" />Asistencia
        </button>
        <button onClick={() => setView("history")} className={`flex-1 py-2.5 rounded-xl text-sm font-semibold transition-all ${view === "history" ? "text-white shadow-md" : "bg-slate-100 text-slate-500"}`} style={view === "history" ? { backgroundColor: primaryColor } : {}} data-testid="tab-history">
          <History className="w-4 h-4 inline mr-1.5" />Historial
        </button>
      </div>

      <div className="px-5 space-y-3 mb-5" data-testid="mobile-preferences-card">
        <Card className="border-0 shadow-sm"><CardContent className="p-4 space-y-3">
          <div className="flex items-center justify-between"><span className="text-sm text-slate-700">Email</span><input type="checkbox" checked={!!preferences.email_opt_in} onChange={e => setPreferences(p => ({ ...p, email_opt_in: e.target.checked }))} data-testid="mobile-email-optin" /></div>
          <div className="flex items-center justify-between"><span className="text-sm text-slate-700">WhatsApp</span><input type="checkbox" checked={!!preferences.whatsapp_opt_in} onChange={e => setPreferences(p => ({ ...p, whatsapp_opt_in: e.target.checked }))} data-testid="mobile-whatsapp-optin" /></div>
          <div className="flex items-center justify-between"><span className="text-sm text-slate-700">Chat interno</span><input type="checkbox" checked={!!preferences.internal_chat_opt_in} onChange={e => setPreferences(p => ({ ...p, internal_chat_opt_in: e.target.checked }))} data-testid="mobile-chat-optin" /></div>
          <div className="flex gap-2"><Button variant="outline" className="flex-1" onClick={connectCalendly} data-testid="mobile-calendly-connect-btn"><CalendarPlus className="w-4 h-4 mr-2" />Calendly</Button><Button className="flex-1" style={{ backgroundColor: primaryColor }} onClick={savePreferences} data-testid="mobile-save-preferences-btn"><BellRing className="w-4 h-4 mr-2" />Guardar</Button></div>
        </CardContent></Card>
        {internalNotifications.length > 0 && <Card className="border-0 shadow-sm"><CardContent className="p-4"><p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">Notificaciones internas</p>{internalNotifications.slice(0,3).map(item => <div key={item.notification_id} className="mb-2"><p className="text-sm font-medium text-slate-900">{item.title}</p><p className="text-xs text-slate-500">{item.body}</p></div>)}</CardContent></Card>}
      </div>

      {view === "home" ? (
        <div className="px-5 space-y-4">
          {/* ====== TODAY STATUS CARD ====== */}
          <Card className="border-0 shadow-lg overflow-hidden" data-testid="today-status-card">
            <div className="h-1.5" style={{ background: `linear-gradient(90deg, ${primaryColor}, ${primaryColor}80)` }} />
            <CardContent className="p-5">
              <div className="text-center">
                <p className="text-xs text-slate-400 uppercase tracking-wider font-semibold mb-1">
                  {new Date().toLocaleDateString("es-MX", { weekday: "long", day: "numeric", month: "long" })}
                </p>
                {todayEntry ? (
                  <div className="space-y-3 mt-4">
                    <div className="flex items-center justify-center gap-6">
                      <div className="text-center">
                        <p className="text-[10px] text-slate-400 uppercase font-semibold">Entrada</p>
                        <p className="text-2xl font-bold text-slate-900 font-mono">{todayEntry.check_in}</p>
                      </div>
                      <ChevronRight className="w-5 h-5 text-slate-300" />
                      <div className="text-center">
                        <p className="text-[10px] text-slate-400 uppercase font-semibold">Salida</p>
                        <p className="text-2xl font-bold text-slate-900 font-mono">{todayEntry.check_out || "--:--"}</p>
                      </div>
                    </div>
                    <div className="flex items-center justify-center gap-3">
                      <Badge className={todayEntry.status === "present" ? "bg-emerald-100 text-emerald-700" : todayEntry.status === "late" ? "bg-amber-100 text-amber-700" : "bg-red-100 text-red-700"}>
                        {todayEntry.status === "present" ? "A tiempo" : todayEntry.status === "late" ? "Retardo" : "Ausente"}
                      </Badge>
                      {todayEntry.total_hours && <Badge className="bg-blue-100 text-blue-700">{todayEntry.total_hours}h trabajadas</Badge>}
                      {todayEntry.source === "mobile_gps" && <Badge className="bg-violet-100 text-violet-700"><MapPin className="w-3 h-3 mr-1" />GPS</Badge>}
                    </div>
                  </div>
                ) : (
                  <div className="mt-4">
                    <div className="w-16 h-16 mx-auto rounded-full bg-slate-100 flex items-center justify-center mb-3">
                      <Clock className="w-8 h-8 text-slate-300" />
                    </div>
                    <p className="text-slate-500 text-sm">Sin registros hoy</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* ====== ACTION BUTTON ====== */}
          <div className="pt-2">
            {!isClockedIn ? (
              <button onClick={handleClockIn} disabled={actionLoading || !gps.coords}
                className="w-full py-5 rounded-2xl text-white font-bold text-lg shadow-xl transition-all active:scale-[0.97] disabled:opacity-50 flex items-center justify-center gap-3"
                style={{ backgroundColor: primaryColor, boxShadow: `0 10px 30px ${primaryColor}40` }} data-testid="clock-in-btn">
                {actionLoading ? <Loader2 className="w-6 h-6 animate-spin" /> : <LogIn className="w-6 h-6" />}
                {actionLoading ? "Registrando..." : todayEntry?.check_out ? "Ya registraste hoy" : "Registrar Entrada"}
              </button>
            ) : (
              <button onClick={handleClockOut} disabled={actionLoading || !gps.coords}
                className="w-full py-5 rounded-2xl text-white font-bold text-lg shadow-xl transition-all active:scale-[0.97] disabled:opacity-50 flex items-center justify-center gap-3 bg-slate-800"
                style={{ boxShadow: "0 10px 30px rgba(0,0,0,0.2)" }} data-testid="clock-out-btn">
                {actionLoading ? <Loader2 className="w-6 h-6 animate-spin" /> : <LogOut className="w-6 h-6" />}
                {actionLoading ? "Registrando..." : "Registrar Salida"}
              </button>
            )}
            {!gps.coords && <p className="text-center text-xs text-red-500 mt-2">Activa el GPS para registrar asistencia</p>}
          </div>

          {/* ====== GEOFENCE MAP PLACEHOLDER ====== */}
          {status?.geofences?.length > 0 && gps.coords && (
            <Card className="border-0 shadow-md" data-testid="geofence-map">
              <CardContent className="p-4">
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">Ubicaciones Permitidas</p>
                <div className="space-y-2">
                  {status.geofences.map((g, i) => {
                    const dist = gps.coords ? haversineJS(gps.coords.latitude, gps.coords.longitude, g.latitude, g.longitude) : null;
                    const isInside = dist !== null && dist <= g.radius_meters;
                    return (
                      <div key={i} className={`flex items-center gap-3 p-3 rounded-xl border ${isInside ? "bg-emerald-50 border-emerald-200" : "bg-red-50 border-red-200"}`}>
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${isInside ? "bg-emerald-100" : "bg-red-100"}`}>
                          {isInside ? <CheckCircle2 className="w-4 h-4 text-emerald-600" /> : <XCircle className="w-4 h-4 text-red-600" />}
                        </div>
                        <div className="flex-1">
                          <p className="text-sm font-semibold text-slate-900">{g.name}</p>
                          <p className="text-[10px] text-slate-500">Radio: {g.radius_meters}m{dist !== null ? ` | Distancia: ${Math.round(dist)}m` : ""}</p>
                        </div>
                        <Badge className={isInside ? "bg-emerald-100 text-emerald-700 text-[10px]" : "bg-red-100 text-red-700 text-[10px]"}>
                          {isInside ? "Dentro" : "Fuera"}
                        </Badge>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          )}

          {/* ====== INSTALL PWA PROMPT ====== */}
          <Card className="border-0 shadow-sm bg-gradient-to-r from-blue-50 to-violet-50" data-testid="install-prompt">
            <CardContent className="p-4">
              {installPrompt ? (
                <button onClick={async () => { installPrompt.prompt(); const r = await installPrompt.userChoice; if (r.outcome === "accepted") setInstallPrompt(null); }}
                  className="w-full flex items-center gap-3 text-left" data-testid="install-pwa-btn">
                  <div className="w-10 h-10 rounded-xl bg-blue-600 flex items-center justify-center shrink-0"><Download className="w-5 h-5 text-white" /></div>
                  <div className="flex-1">
                    <p className="text-sm font-bold text-slate-900">Instalar App</p>
                    <p className="text-[10px] text-slate-500">Toca aqui para instalar en tu pantalla de inicio</p>
                  </div>
                  <ChevronRight className="w-5 h-5 text-blue-500" />
                </button>
              ) : (
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-slate-200 flex items-center justify-center shrink-0"><Smartphone className="w-5 h-5 text-slate-500" /></div>
                  <div>
                    <p className="text-xs font-semibold text-slate-700">Instala la app</p>
                    <p className="text-[10px] text-slate-500">En tu navegador, toca el menu y selecciona "Agregar a pantalla de inicio"</p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      ) : (
        /* ====== HISTORY VIEW ====== */
        <div className="px-5 space-y-2" data-testid="history-view">
          <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">Ultimos 7 dias</p>
          {(!status?.history || status.history.length === 0) ? (
            <div className="text-center py-12 text-slate-400">
              <History className="w-10 h-10 mx-auto mb-2 opacity-50" />
              <p className="text-sm">Sin registros recientes</p>
            </div>
          ) : (
            status.history.map((entry, i) => (
              <Card key={i} className="border-0 shadow-sm">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-semibold text-slate-900">{new Date(entry.date + "T12:00:00").toLocaleDateString("es-MX", { weekday: "short", day: "numeric", month: "short" })}</p>
                      <p className="text-xs text-slate-500 mt-0.5 font-mono">{entry.check_in || "--:--"} - {entry.check_out || "--:--"}</p>
                    </div>
                    <div className="text-right flex items-center gap-2">
                      {entry.total_hours && <span className="text-sm font-bold text-slate-700">{entry.total_hours}h</span>}
                      <Badge className={`text-[10px] ${entry.status === "present" ? "bg-emerald-100 text-emerald-700" : entry.status === "late" ? "bg-amber-100 text-amber-700" : "bg-red-100 text-red-700"}`}>
                        {entry.status === "present" ? "OK" : entry.status === "late" ? "Tarde" : "Aus."}
                      </Badge>
                      {entry.source === "mobile_gps" && <MapPin className="w-3.5 h-3.5 text-violet-400" />}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      )}

      {/* ====== BOTTOM BAR ====== */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-slate-100 px-5 py-3 flex items-center justify-between" data-testid="mobile-bottom-bar">
        <div className="flex items-center gap-1.5">
          <Shield className="w-3.5 h-3.5" style={{ color: primaryColor }} />
          <span className="text-[10px] text-slate-400">Seguro y encriptado</span>
        </div>
        <div className="flex items-center gap-1.5">
          <Smartphone className="w-3.5 h-3.5 text-slate-300" />
          <span className="text-[10px] text-slate-400">{branding?.wl_app_name || "Human-OZ"} Mobile</span>
        </div>
      </div>
    </div>
  );
};

/* Haversine in JS for client-side distance */
function haversineJS(lat1, lon1, lat2, lon2) {
  const R = 6371000;
  const p1 = lat1 * Math.PI / 180, p2 = lat2 * Math.PI / 180;
  const dp = (lat2 - lat1) * Math.PI / 180, dl = (lon2 - lon1) * Math.PI / 180;
  const a = Math.sin(dp / 2) ** 2 + Math.cos(p1) * Math.cos(p2) * Math.sin(dl / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

export default MobileApp;
